"use client";

import { useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { CircleAlert, CircleCheck } from "lucide-react";
import { btn } from "@/lib/button-styles";
import { useT } from "@/lib/i18n/client";

// Pop-up for the outcome of the registration-email link: app/verify-email
// redirects home with ?verified=confirmed|already|expired|invalid.
const OUTCOMES = ["confirmed", "already", "expired", "invalid"] as const;

export function EmailVerifiedNotice() {
  const t = useT().auth.emailVerified;
  const params = useSearchParams();
  const pathname = usePathname();
  const [dismissed, setDismissed] = useState(false);

  const value = params.get("verified");
  const outcome = OUTCOMES.find((o) => o === value);
  if (!outcome || dismissed) return null;

  const ok = outcome === "confirmed" || outcome === "already";
  const copy = {
    confirmed: [t.confirmedTitle, t.confirmedBody],
    already: [t.alreadyTitle, t.alreadyBody],
    expired: [t.expiredTitle, t.expiredBody],
    invalid: [t.invalidTitle, t.invalidBody],
  }[outcome];
  const Icon = ok ? CircleCheck : CircleAlert;

  function close() {
    setDismissed(true);
    // Drop the param so a reload doesn't show the pop-up again.
    const rest = new URLSearchParams(params);
    rest.delete("verified");
    const query = rest.toString();
    window.history.replaceState(null, "", query ? `${pathname}?${query}` : pathname);
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={close}>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="email-verified-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-xl border border-border bg-white p-6 text-center shadow-2xl"
      >
        <Icon className={`mx-auto size-10 ${ok ? "text-success" : "text-destructive"}`} aria-hidden />
        <h2 id="email-verified-title" className="mt-3 text-[17px] font-bold text-foreground">
          {copy[0]}
        </h2>
        <p className="mt-1.5 text-[13.5px] text-muted-foreground">{copy[1]}</p>
        <button type="button" onClick={close} autoFocus className={btn("primary", "md", "mt-5 w-full justify-center")}>
          {t.close}
        </button>
      </div>
    </div>
  );
}
